import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// What the invoice widgets' date fields share: their limits, their message,
// the ISO day they send, and how a server message under one gives way. So
// the fields cannot drift apart in their rules or their wording.

/** Why a date field refuses text the date adapter cannot read. */
export const DATE_UNREADABLE = 'not a valid date';

// No invoice date is plausibly earlier. A year typed with a digit missing or
// mistyped, 1.1.0202 or 1.1.1202, is.
const EARLIEST_PLAUSIBLE_DATE = '2000-01-01';

/** Why a date field refuses a day before its earliest one. */
export const DATE_BEFORE_EARLIEST = 'must not be before 1.1.2000';

/**
 * The date fields of one invoice widget's form, as its template binds them:
 * the earliest day each picker accepts, and why each field refuses its value.
 */
export class DateFields<F extends string> {
  /**
   * @param form The widget's form, with a datepicker control for each field.
   * @param stored The field's stored day, an ISO string as the API gives it.
   * @param pastLatest For a field with a `[max]`, names the limit a typed day
   *   (as `toIsoDate` renders it) is past.
   */
  constructor(
    private readonly form: UntypedFormGroup,
    private readonly stored: (field: F) => string | null | undefined,
    private readonly pastLatest: Partial<Record<F, (typed: string | null) => string>> = {},
  ) {}

  /**
   * The earliest day the field's picker accepts: 1.1.2000, or the field's
   * stored day when that is earlier. `[min]` is also a validator, so a typed
   * day before it keeps the form from saving; a legacy stored day before 2000
   * stays valid, so it never blocks an unrelated save. The ISO string suits
   * both the app's MomentDateAdapter and the native one as a picker's `[min]`.
   */
  public earliest(field: F): string {
    const stored = this.stored(field);
    return stored && stored < EARLIEST_PLAUSIBLE_DATE ? stored : EARLIEST_PLAUSIBLE_DATE;
  }

  /**
   * Why the field refuses its value, or null when it does not: text the date
   * adapter cannot read, a day before its `[min]`, or a day past its `[max]`,
   * named by its `pastLatest`. One message, for whatever is wrong.
   */
  public message(field: F): string | null {
    const control: AbstractControl = this.form.controls[field];
    if (control.hasError('matDatepickerParse')) {
      return DATE_UNREADABLE;
    }
    if (control.hasError('matDatepickerMin')) {
      return DATE_BEFORE_EARLIEST;
    }
    const pastLatest = this.pastLatest[field];
    if (control.hasError('matDatepickerMax') && pastLatest) {
      return pastLatest(toIsoDate(control.value));
    }
    return null;
  }
}

/**
 * Clears a server field error once the handler edits its field, until
 * `destroy$` emits. The server's refusal is about the value that was sent, so
 * from the first edit the field's own check speaks for the new value: the two
 * never show at once, and a fixed value keeps no stale refusal under it.
 * Editing another field keeps it.
 */
export function clearFieldErrorOnEdit(
  form: UntypedFormGroup,
  fields: readonly string[],
  host: { fieldErrors: Record<string, string> },
  destroy$: Observable<void>,
): void {
  for (const field of fields) {
    form.controls[field].valueChanges.pipe(takeUntil(destroy$)).subscribe(() => {
      if (!(field in host.fieldErrors)) {
        return;
      }
      const rest = { ...host.fieldErrors };
      delete rest[field];
      host.fieldErrors = rest;
    });
  }
}

/**
 * A datepicker value (a Date, or the Moment the app's MomentDateAdapter
 * produces) as its calendar day in ISO `YYYY-MM-DD`, read with the local
 * getters. The year is padded to four digits, so a year before 1000 still
 * makes a string the server can read. Null for an empty value.
 */
export function toIsoDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const d = value instanceof Date ? value : new Date(value as string);
  const yyyy = String(d.getFullYear()).padStart(4, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
